import { Contract, hash, Call, AccountInterface, ProviderInterface, CallData } from 'starknet';
import DeferredBatchCallAdapter_ABI from './abi/DeferredBatchCallAdapter_abi.json';
import { Asset, InvokeResponse, Nullable } from '../../interfaces';
import { Decimal } from '../../datastructures';
import { getNostraTokenFromFlags, getAccountAddressFromId, executeTransaction, toHex } from '../../utils';

export class DeferredBatchCallAdapterContract {
  private contract: Contract;

  constructor(address: string, providerOrAccount: ProviderInterface | AccountInterface) {
    this.contract = new Contract({ abi: DeferredBatchCallAdapter_ABI, address, providerOrAccount });
  }

  async burnAndMint(
    asset: Asset,
    tokenPrecision: number,
    amount: Decimal,
    accountId: number,
    isFromInterestBearing: boolean,
    isFromCollateral: boolean,
    isToInterestBearing: boolean,
    isToCollateral: boolean,
    account: AccountInterface,
  ): Promise<Nullable<InvokeResponse>> {
    if (!this.contract) {
      return null;
    }

    const accountAddress = getAccountAddressFromId(account.address, accountId);

    const fromNostraToken = getNostraTokenFromFlags(asset, {
      interestBearing: isFromInterestBearing,
      collateral: isFromCollateral,
    });
    const toNostraToken = getNostraTokenFromFlags(asset, {
      interestBearing: isToInterestBearing,
      collateral: isToCollateral,
    });

    const amountUint256 = amount.toUint256(tokenPrecision);
    // const maxDecimalUint256 = Decimal.MAX_DECIMAL.toUint256(tokenPrecision);
    const burnAndMintAccountsToDefer = [account.address, this.contract.address];

    const burnAndMintCalls = [
      // transfer from
      {
        to: fromNostraToken.address,
        selector: hash.getSelectorFromName('transferFrom'),
        calldata: [accountAddress, this.contract.address, amountUint256.low, amountUint256.high],
      },
      // withdraw
      {
        to: fromNostraToken.address,
        selector: hash.getSelectorFromName('burn'),
        calldata: [this.contract.address, this.contract.address, amountUint256.low, amountUint256.high],
      },
      // approve
      {
        to: asset.address,
        selector: hash.getSelectorFromName('approve'),
        calldata: [toNostraToken.address, amountUint256.low, amountUint256.high],
      },
      // deposit
      {
        to: toNostraToken.address,
        selector: hash.getSelectorFromName('mint'),
        calldata: [accountAddress, amountUint256.low, amountUint256.high],
      },
    ];
    const calls = [
      {
        contractAddress: fromNostraToken.address,
        entrypoint: 'approveFromSubAccount',
        calldata: CallData.compile([accountAddress, this.contract.address, amountUint256.low, amountUint256.high]).map(
          data => toHex(data),
        ),
      },
      {
        contractAddress: this.contract.address,
        entrypoint: 'batch',
        calldata: CallData.compile([burnAndMintAccountsToDefer, burnAndMintCalls]).map(data => toHex(data)),
      },
    ] as Call[];

    return executeTransaction(account, calls);
  }
}
